/**
 * Centralized copy system for PureBox.
 *
 * Loads copy strings from the build-time registry (copy-registry.json),
 * which is the machine-readable mirror of the design-system
 * copy-source-of-truth.json.
 *
 * Usage:
 *   import { getCopy, getCopySection } from '@/lib/copy'
 *
 *   const headline = getCopy('/', 'landing.hero.headline')
 *   const dashStrings = getCopySection('/dashboard')
 */

import registry from './copy-registry.json';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The registry is a map of route → flat key/value strings for getCopy(). */
type CopyRegistry = Record<string, Record<string, string>>;

const copyRegistry: CopyRegistry = registry as unknown as CopyRegistry;

/** All route paths present in the registry. */
export type CopyRoute = keyof typeof registry;

// ---------------------------------------------------------------------------
// Accessors
// ---------------------------------------------------------------------------

/**
 * Retrieve a single copy string by route and key.
 *
 * @param route   The page route, e.g. `'/'`, `'/dashboard'`
 * @param key     The dot-delimited string key, e.g. `'landing.hero.headline'`
 * @param params  Optional placeholder values. The resolved string has any
 *                `{token}` occurrences replaced with `params[token]`. Tokens
 *                without a matching param are left untouched so missing data
 *                is visible at the call-site instead of silently disappearing.
 * @returns       The resolved string, or `key` itself as a fallback so the UI
 *                never renders `undefined`.
 */
export function getCopy(
  route: string,
  key: string,
  params?: Record<string, string | number>,
): string {
  const section = copyRegistry[route];
  if (!section) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[copy] Unknown route: "${route}"`);
    }
    return key;
  }

  const value = section[key];
  if (value === undefined) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[copy] Missing key "${key}" in route "${route}"`);
    }
    return key;
  }

  if (params) {
    return value.replace(/\{(\w+)\}/g, (match, token) => {
      const replacement = params[token];
        if (replacement === undefined) return match;
        if (token === 'potentialCount' && typeof replacement === 'number') {
          return replacement.toLocaleString('en-US');
        }
        return String(replacement);
    });
  }

  return value;
}

/**
 * Retrieve all copy strings for a given route.
 *
 * @param route  The page route
 * @returns      A shallow copy of the key/value map, or an empty object if
 *               the route is unknown.
 */
export function getCopySection(route: string): Record<string, string> {
  const section = copyRegistry[route];
  if (!section) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[copy] Unknown route: "${route}"`);
    }
    return {};
  }
  return { ...section };
}

/**
 * Check whether a route exists in the copy registry.
 */
export function hasCopyRoute(route: string): boolean {
  return route in copyRegistry;
}

/**
 * List all registered routes (useful for build-time validation).
 */
export function listCopyRoutes(): string[] {
  return Object.keys(copyRegistry);
}
